// @givmo/app-admin — the embed snippets + the preview frame URL (CW-WEB4). Pure, no React.
//
// ══ THESE STRINGS LEAVE GIVMO ═════════════════════════════════════════════════════════════════
// Everything this module builds is either COPIED OUT of the portal onto a page Givmo does not
// control, or used as the `src` of a frame. So the two hosts are read from config and NEVER
// defaulted (see `lib/env.ts`): a wrong-environment guess would be pasted somewhere permanent, and
// an unconfigured environment offers no snippet at all rather than a broken one.
//
// The snippet forms are `packages/widget-loader/README.md` § *The snippet* verbatim — the same two
// the integration harness executes against the real embed (`fixtures/host-page.html`). This screen
// only fills in the ids and the hosts.
//
// ⚠ NOTHING HERE IMPORTS `@givmo/widget-loader`. It is an artifact, not a library (its package has
// no `main`/`exports` and nothing in the repo imports it), so its constants are re-stated as literals
// in the emitted HTML. The `embed-base` attribute is deliberately NEVER emitted: the loader validates
// it against a compiled-in origin allow-list and falls back to its own default, so a value we wrote
// would be either redundant or silently ignored.

/**
 * Characters admitted in a widget id — one opaque path segment, no `/`, `?`, `#`, `%`, `.` or
 * whitespace, so nothing in the value can climb out of `/w/{id}` into another route or smuggle a
 * query string onto the document URL.
 *
 * NOT pinned to the `wgt_` prefix: the id space is the backend's, and a screen that rejected a future
 * one would be the thing that broke. Same discipline as the loader's own check, restated rather than
 * imported for the reason in the header.
 */
const SAFE_WIDGET_ID = /^[A-Za-z0-9_-]{1,128}$/;

/** True for a value we are willing to place in an embed URL's path or a pasted snippet. */
export function isSafeWidgetId(value: unknown): value is string {
  return typeof value === 'string' && SAFE_WIDGET_ID.test(value);
}

/**
 * Normalize a configured host origin, or `null` if it is not one we will point a frame at.
 *
 * `https:` only, plus loopback on either scheme so a local embed can be driven during development.
 * A value carrying credentials, a query, a fragment or a path is refused outright rather than being
 * trimmed into something plausible — this is build-time configuration, and a malformed value is a
 * deploy bug to see, not to repair silently.
 */
export function normalizeHostOrigin(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.trim().length === 0) return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  const loopback = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
  if (url.protocol !== 'https:' && !(loopback && url.protocol === 'http:')) return null;
  if (url.username.length > 0 || url.password.length > 0) return null;
  if (url.search.length > 0 || url.hash.length > 0) return null;
  if (url.pathname !== '/' && url.pathname !== '') return null;
  return url.origin;
}

/**
 * Normalize the loader's script URL. Same scheme rule as {@link normalizeHostOrigin}, but a PATH is
 * required here — the artifact is `…/widget-v1.js`, a file, not an origin. Query and fragment are
 * still refused: a cache-buster baked into a snippet on someone else's page outlives the reason it
 * was added.
 */
export function normalizeLoaderUrl(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string' || raw.trim().length === 0) return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  const loopback = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
  if (url.protocol !== 'https:' && !(loopback && url.protocol === 'http:')) return null;
  if (url.username.length > 0 || url.password.length > 0) return null;
  if (url.search.length > 0 || url.hash.length > 0) return null;
  if (url.pathname === '/' || url.pathname === '') return null;
  return url.toString();
}

/**
 * The embed document's URL for one widget — the preview frame's `src`, and the bare-iframe snippet's.
 *
 * `null` whenever either half is unusable, which is what makes an unconfigured environment render no
 * preview instead of a frame pointed at `null/w/undefined`.
 */
export function buildEmbedDocumentUrl(
  embedBase: string | null | undefined,
  widgetId: string | null | undefined,
): string | null {
  const origin = normalizeHostOrigin(embedBase);
  if (origin === null || !isSafeWidgetId(widgetId)) return null;
  return `${origin}/w/${widgetId}`;
}

/**
 * The `<script>` + custom-element snippet — the form to prefer, and the only one whose height is
 * ever right: the loader listens for the frame's height announcement and keeps it in step, so a
 * company that later publishes another block does not have to re-edit its own page.
 *
 * One script tag per page however many widgets follow, and order does not matter — custom elements
 * upgrade, so the tag may appear before or after the script.
 */
export function buildScriptSnippet(
  loaderUrl: string | null | undefined,
  widgetId: string | null | undefined,
): string | null {
  const script = normalizeLoaderUrl(loaderUrl);
  if (script === null || !isSafeWidgetId(widgetId)) return null;
  return [
    `<script src="${script}"></script>`,
    '',
    `<givmo-widget widget-id="${widgetId}" initial-height="${WIDGET_INITIAL_HEIGHT}"></givmo-widget>`,
  ].join('\n');
}

/**
 * The plain-iframe snippet, for a site that cannot run third-party JavaScript.
 *
 * ⚠ ITS HEIGHT IS FIXED AND NOTHING CORRECTS IT. With no loader on the page nothing is listening for
 * the frame's height announcement, so `height` is whatever this snippet sets, forever. It is
 * therefore sized to the MEASURED company maximum rather than to the loader's cause-card default —
 * see {@link IFRAME_FALLBACK_HEIGHT}. The tab still says the frame is fixed, because a company that
 * publishes few blocks will want to trim it.
 *
 * The `sandbox` tokens are the loader's, and each is load-bearing: `allow-scripts` so the card can
 * measure itself, `allow-same-origin` so the embed's own `script-src 'self'` can match (an opaque
 * origin blocks its hydration bundle), and the two popup tokens so the donate CTA can open the Givmo
 * hosted checkout in a real top-level window rather than a storage-less opaque one.
 */
export function buildIframeSnippet(
  embedBase: string | null | undefined,
  widgetId: string | null | undefined,
  frameTitle: string,
): string | null {
  const src = buildEmbedDocumentUrl(embedBase, widgetId);
  if (src === null) return null;
  return [
    '<iframe',
    `  src="${src}"`,
    `  title="${escapeAttribute(frameTitle)}"`,
    '  width="100%"',
    `  height="${IFRAME_FALLBACK_HEIGHT}"`,
    '  style="border: 0; display: block"',
    '  loading="lazy"',
    '  referrerpolicy="strict-origin-when-cross-origin"',
    '  sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"',
    '  scrolling="no"',
    '></iframe>',
  ].join('\n');
}

/**
 * The bare iframe's fixed height, in CSS pixels. MEASURED, not the loader's default (CW-WEB7).
 *
 * ⚠ THE LOADER'S 300px DEFAULT IS A CAUSE-CARD NUMBER AND CLIPS EVERY REAL COMPANY CARD. Measured
 * against the real embed by `packages/widget-loader/scripts/verify-loader-integration.mjs` at a
 * 280px column, 2026-08-02: a suppressed or zero-participation company card is 343px, a full one is
 * 368px, and the maximum an Owner can publish — all six blocks, the backend's five-charity cap
 * (`TOP_CHARITY_LIMIT`), its six-cause cap (`FEATURED_CAUSE_LIMIT`) and the donate CTA — is 695px.
 * The first cut of this screen emitted 300, which would have clipped the smallest real card by 43px
 * on the ONE form that never resizes itself.
 *
 * ⚠ THE CTA IS IN THAT NUMBER ON PURPOSE, AND IT IS WORTH 60px. No captured payload carries one
 * (`cta: null` everywhere — CW-BE7 unarmed, charging dark), but D-CW-4 permits a company card to
 * carry one and both the decode and the render path already exist, so the day charging goes live the
 * card grows on pages whose snippet was pasted months earlier and can never be corrected. Sizing to
 * the pre-CTA 635 would have clipped that card by 35px. The harness measures the CTA variant, so
 * this stays honest without anyone remembering to revisit it.
 *
 * 720 clears the maximum. It is deliberately erring toward blank space rather than clipping,
 * because the two failure directions are not symmetric: a frame that is too tall is visibly and
 * obviously trimmable by the host (and the copy beside the snippet says so), while one that is too
 * short silently truncates the company's own published figures on the company's own page.
 */
export const IFRAME_FALLBACK_HEIGHT = 720;

/**
 * The `initial-height` hint on the `<givmo-widget>` form — a DIFFERENT number, for a different job.
 *
 * That form resizes itself the moment the embed announces, so this value is only the first paint and
 * the goal is the smallest visible jump: the EXPECTED height (a real published card, 343–368px),
 * not the ceiling. Using 720 here would make every company card visibly collapse on load, which is a
 * worse first impression than the ~30px settle this leaves.
 */
export const WIDGET_INITIAL_HEIGHT = 380;

/**
 * Escape a value going into a double-quoted HTML attribute inside a snippet.
 *
 * The only interpolated non-id value is the frame title, which this screen authors — but the snippet
 * is HTML the Owner pastes into their own page, so the escape belongs at the point of emission
 * rather than resting on the current caller staying the current caller.
 */
function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
